import { FC, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import { DocumentDetails } from 'src/interfaces/new-channel-partner/channelPartner';
import { InputField } from 'src/components/input-field';
import { SUPPORTED_FORMATS } from 'src/enums/common';
import { Scrollbar } from 'src/components/scrollbar';
import { useDialog } from 'src/hooks/use-dialog';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { IdValue } from 'src/interfaces/common/IdValue';
import toast from 'react-hot-toast';
import { Trash as TrashIcon } from '../../../../icons/trash';

interface AddKycDocumentsProps {
  open: boolean;
  onClose: () => void;
  scope?: string;
  documentDetails: DocumentDetails[];
  addDocument: (data: DocumentDetails, selectedFile: any) => Promise<boolean>;
  deleteDocument: (data: string) => Promise<boolean>;
}

export const AddKycDocuments: FC<AddKycDocumentsProps> = (props) => {
  const { open, onClose, scope, documentDetails, addDocument, deleteDocument } = props;
  const [aadharDoc, setAadharDoc] = useState<DocumentDetails>();
  const [panCardDoc, setPanCardDoc] = useState<DocumentDetails>();
  const [gstDoc, setGstDoc] = useState<DocumentDetails>();
  const [optionalDocs, setOptionalDocs] = useState<DocumentDetails[]>([]);
  const [isOptionalClicked, setIsOptionalClicked] = useState<boolean>(false);
  const [serviceAreaToBeDeleted, setServiceAreaToBeDeleted] = useState<IdValue>();
  const FileName = styled('p')(() => ({
    fontSize: 14,
    marginTop: 0,
    marginBottom: 0,
    textOverflow: 'ellipsis',
    width: '150px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'inline-block'
  }));

  const columns = [
    {
      id: 1,
      label: 'DOCUMENT TYPE'
    },
    {
      id: 2,
      label: 'DOCUMENT NUMBER'
    },
    {
      id: 3,
      label: 'Select DOCUMENT'
    },
    {
      id: 4,
      label: 'Upload DOCUMENT'
    }
  ];

  const KycDocumentsInitialValues = {
    aadharCard: {
      id: aadharDoc?._id || '',
      docType: 'Aadhar Card',
      docNumber: aadharDoc?.docNumber || '',
      file: aadharDoc?.file || null,
      mandatory: true,
      fileName: aadharDoc?.file?.name || '',
      docFile: null
    },
    panCard: {
      id: panCardDoc?._id || '',
      docType: 'PAN Card',
      docNumber: panCardDoc?.docNumber || '',
      file: panCardDoc?.file || null,
      mandatory: true,
      fileName: panCardDoc?.file?.name || '',
      docFile: null
    },
    gst: {
      id: '',
      docType: 'GST',
      docNumber: '',
      file: null,
      mandatory: true,
      fileName: '',
      docFile: null
    },
    optional: {
      docType: '',
      docNumber: '',
      file: null,
      mandatory: false,
      fileName: '',
      docFile: null
    },
    submit: null,
  };

  const isNumeric = (num) => /^\d+$/.test(num);

  const setDetails = () => {
    const aadharDetail = documentDetails.filter((item) => item.docType === 'Aadhar Card')[0];
    const panDetail = documentDetails.filter((item) => item.docType === 'PAN Card')[0];
    const gstDetail = documentDetails.filter((item) => item.docType === 'GST')[0];
    const optionalDocsDetails = documentDetails.filter((item) => !item.mandatory);
    if (optionalDocsDetails[0]?.docNumber) {
      setOptionalDocs(optionalDocsDetails);
    } else {
      setOptionalDocs([]);
    }

    if (gstDetail?._id) {
      setGstDoc(gstDetail);
    } else {
      setGstDoc({
        _id: '',
        docType: '',
        docNumber: '',
        file: {
          name: '',
          key: '',
          type: '',
        },
        mandatory: true
      });
    }
    if (aadharDetail?._id) {
      setAadharDoc(aadharDetail);
    } else {
      setAadharDoc({
        _id: '',
        docType: '',
        docNumber: '',
        file: {
          name: '',
          key: '',
          type: '',
        },
        mandatory: true
      });
    }
    if (panDetail?._id) {
      setPanCardDoc(panDetail);
    } else {
      setPanCardDoc({
        _id: '',
        docType: '',
        docNumber: '',
        file: {
          name: '',
          key: '',
          type: '',
        },
        mandatory: true
      });
    }
  };

  useEffect(() => {
    if (documentDetails.length) {
      setDetails();
    }
  }, [documentDetails]);
  const docs = new Set<string>();
  documentDetails.map((doc) => docs.add(doc.docType));
  docs.add(KycDocumentsInitialValues.aadharCard.docType);
  docs.add(KycDocumentsInitialValues.panCard.docType);
  if (scope !== 'Technician') {
    docs.add(KycDocumentsInitialValues.gst.docType);
  }
  const formik = useFormik({
    enableReinitialize: false,
    initialValues: KycDocumentsInitialValues,
    validationSchema: Yup.object().shape({
      aadharCard: Yup.object().shape({
        docNumber: Yup.string()
          .required('Document Number is required')
          .test(
            'aadharCard.docNumber',
            'Must be in digits',
            (value) => isNumeric(value)
          )
          .typeError('Must be Digits'),
        file: Yup.mixed().test(
          'fileFormat',
          'File Format is not supported',
          (value) => value && SUPPORTED_FORMATS.includes(value.type)
        ).required('File is required'),
      }),
      panCard: Yup.object().shape({
        docNumber: Yup.string()
          .required('Document Number is required').matches(new RegExp(/^[A-Za-z0-9]+$/),
            'Only alphabets and Numbers are allowed'),
        file: Yup.mixed().test(
          'fileFormat',
          'File Format is not supported',
          (value) => value && SUPPORTED_FORMATS.includes(value.type)
        ).required('File is required'),
      }),
      gst: Yup.object().shape({
        docNumber: Yup.string().required('Document Number is required')
          .matches(new RegExp(/^[A-Za-z0-9]+$/), 'Only alphabets and Numbers are allowed'),
        file: Yup.mixed().test(
          'fileFormat',
          'File Format is not supported',
          (value) => value && SUPPORTED_FORMATS.includes(value.type)
        ).required('File is required'),
      }),
      optional: Yup.object().shape({
        docType: Yup.string().max(255).required('Document Type Required').matches(new RegExp(/^[A-Za-z0-9 ]+$/),
          'Only alphabets and spaces allowed')
          .notOneOf(Array.from(docs), 'Document type must be unique'),
        docNumber: Yup.string()
          .required('Document Number is required')
          .matches(new RegExp(/^[A-Za-z0-9]+$/), 'Only alphabets and Numbers are allowed')
          .typeError('Document type must be string'),
        file: Yup.mixed().test(
          'fileFormat',
          'File Format is not supported',
          (value) => value && SUPPORTED_FORMATS.includes(value.type)
        ).required('File is required')
          .typeError('Document number must be alphanumeric'),
      }),
      submit: null,
    }),
    onSubmit: async () => {
    }
  });

  const addDocuments = async (data) => {
    const doc: DocumentDetails = {
      docType: data.docType,
      docNumber: data.docNumber,
      file: {
        name: data.file.name,
        key: '',
        type: data.file.type
      },
      mandatory: data.mandatory
    };
    const addDoc = await addDocument(doc, data.docFile);
    if (addDoc) {
      if (!data.mandatory) {
        setIsOptionalClicked(false);
        await formik.setFieldValue('optional', {});
        await formik.setFieldValue('optional.file', null);
        await formik.setFieldValue('optional.docType', '');
        await formik.setFieldValue('optional.docNumber', '');
        await formik.setFieldError('optional.docType', undefined);
        await formik.setFieldError('optional.docNumber', undefined);
      }
    }
  };

  const handleDeleteSelectedFile = async (type) => {
    if (type === 'Aadhar Card') {
      await formik.setFieldValue('aadharCard.docFile', null);
      await formik.setFieldValue('aadharCard.file', null);
    } else if (type === 'PAN Card') {
      await formik.setFieldValue('panCard.docFile', null);
      await formik.setFieldValue('panCard.file', null);
    } else if (type === 'GST') {
      await formik.setFieldValue('gst.docFile', null);
      await formik.setFieldValue('gst.file', null);
    } else {
      await formik.setFieldValue('optional.docFile', null);
      await formik.setFieldValue('optional.file', null);
    }
  };

  const handleFileUpload = async (e, type) => {
    if (SUPPORTED_FORMATS.includes(e.target.files[0].type)) {
      if (type === 'Aadhar Card') {
        await formik.setFieldValue('aadharCard.docFile', e.target.files[0]);
        await formik.setFieldValue('aadharCard.file.type', e.target.files[0].type);
        await formik.setFieldValue('aadharCard.file.name', e.target.files[0].name);
      } else if (type === 'PAN Card') {
        await formik.setFieldValue('panCard.docFile', e.target.files[0]);
        await formik.setFieldValue('panCard.file.type', e.target.files[0].type);
        await formik.setFieldValue('panCard.file.name', e.target.files[0].name);
      } else if (type === 'GST') {
        await formik.setFieldValue('gst.docFile', e.target.files[0]);
        await formik.setFieldValue('gst.file.type', e.target.files[0].type);
        await formik.setFieldValue('gst.file.name', e.target.files[0].name);
      } else {
        await formik.setFieldValue('optional.docFile', e.target.files[0]);
        await formik.setFieldValue('optional.file.type', e.target.files[0].type);
        await formik.setFieldValue('optional.file.name', e.target.files[0].name);
      }
    } else {
      toast.error('File Formated not Supported');
    }
  };

  const handleDeleteItem = (id: string, type: string) => {
    const val = {
      id,
      type
    };
    setServiceAreaToBeDeleted(val);
  };

  const clearOptionalField = async () => {
    await formik.setFieldValue('optional.file', null);
    await formik.setFieldTouched('optional', false);
    await formik.setFieldError('optional', null);
    await formik.setFieldValue('optional.docType', null);
    await formik.setFieldValue('optional.docNumber', null);
    setIsOptionalClicked(false);
  };
  const [
    deleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog
  ] = useDialog();

  useEffect(() => {
    if (serviceAreaToBeDeleted?.id) {
      handleOpenDeleteDialog();
    }
  }, [serviceAreaToBeDeleted]);

  const handleDocumentDelete = async (id: string, type: string) => {
    const deleteResult = deleteDocument(id);
    if (deleteResult) {
      handleCloseDeleteDialog();
      if (type === 'Aadhar Card') {
        setAadharDoc({
          _id: '',
          docType: '',
          docNumber: '',
          file: {
            name: '',
            key: '',
            type: '',
          },
          mandatory: true
        });
        await formik.setFieldValue('aadharCard.docNumber', '');
        await formik.setFieldTouched('aadharCard.docNumber', false);
        handleDeleteSelectedFile('Aadhar Card');
      } else if (type === 'PAN Card') {
        setPanCardDoc({
          _id: '',
          docType: '',
          docNumber: '',
          file: {
            name: '',
            key: '',
            type: '',
          },
          mandatory: true
        });
        await formik.setFieldValue('panCard.docNumber', '');
        await formik.setFieldTouched('panCard.docNumber', false);
        handleDeleteSelectedFile('PAN Card');
      } else if (type === 'GST') {
        setGstDoc({
          _id: '',
          docType: '',
          docNumber: '',
          file: {
            name: '',
            key: '',
            type: '',
          },
          mandatory: true
        });
        await formik.setFieldValue('gst.docNumber', '');
        await formik.setFieldTouched('gst.docNumber', false);
        handleDeleteSelectedFile('GST');
      } else {
        await formik.setFieldValue('optional.docNumber', '');
        await formik.setFieldTouched('optional.docNumber', false);
        handleDeleteSelectedFile('');
      }
    }
  };

  // this method maintain the selection when we close the dialog
  const closeDialog = async (e, reason) => {
    if (reason !== 'backdropClick') {
      setAadharDoc({
        _id: '',
        docType: '',
        docNumber: '',
        file: {
          name: '',
          key: '',
          type: '',
        },
        mandatory: true
      });
      setPanCardDoc({
        _id: '',
        docType: '',
        docNumber: '',
        file: {
          name: '',
          key: '',
          type: '',
        },
        mandatory: true
      });
      setGstDoc({
        _id: '',
        docType: '',
        docNumber: '',
        file: {
          name: '',
          key: '',
          type: '',
        },
        mandatory: true
      });
      await formik.setFieldValue('aadharCard.file', null);
      await formik.setFieldValue('optional.file', null);
      await formik.setFieldValue('gst.file', null);
      await formik.setFieldValue('panCard.file', null);
      onClose();
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={closeDialog}
      open={open}
    >
      <DialogTitle
        sx={{ justifyContent: 'flex-end' }}
      >
        KYC Documents
        <IconButton
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
          color="inherit"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          mt: -2
        }}
      >
        <Grid
          container
          spacing={1}
          py={1}
          justifyContent="end"
          alignItems="end"
        >
          <Grid
            item
            md={12}
            sm={12}
            xs={12}
            p={1}
            display="contents"
          >
            <Button
              color="primary"
              disabled={isOptionalClicked}
              onClick={() => setIsOptionalClicked(true)}
              variant="contained"
            >
              Add Optional Documents
            </Button>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={1}
        >
          <Grid
            item
            md={12}
            sm={12}
            xs={12}
          >
            <Card
              variant="outlined"
            >
              <Scrollbar>
                <TableContainer
                  sx={{
                    maxHeight: '220px',
                  }}
                >
                  <Table
                    size="small"
                  >
                    <TableHead>
                      <TableRow>
                        {columns?.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.id === 4 ? 'center' : 'left'}
                            width={column.id === 1 || column.id === 2 ? '25%' : '25%'}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <>
                        <TableRow>
                          <TableCell>
                            {formik.values.aadharCard?.docType}
                            <Typography
                              display="contents"
                              color="red"
                            >
                              &nbsp;*
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {aadharDoc?.docNumber ? (
                              aadharDoc?.docNumber
                            ) : (
                              <InputField
                                error={Boolean(formik.touched.aadharCard?.docNumber
                                && formik.errors.aadharCard?.docNumber)}
                                fullWidth
                                required
                                helperText={formik.touched.aadharCard?.docNumber
                                && formik.errors.aadharCard?.docNumber}
                                name="aadharCard.docNumber"
                                onBlur={formik.handleBlur}
                                onChange={(event) => {
                                  if (event.target.value !== ' ') {
                                    formik.setFieldValue('aadharCard.docNumber', event.target.value);
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {aadharDoc?.docNumber ? (
                              <FileName>
                                {aadharDoc?.file?.name}
                              </FileName>
                            ) : (
                              <>
                                {(formik.values.aadharCard?.file !== null) ? (
                                  <Stack
                                    direction="row"
                                  >
                                    <FileName>
                                      {formik.values.aadharCard?.file?.name}
                                    </FileName>
                                    <IconButton
                                      aria-label="delete"
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteSelectedFile('Aadhar Card')}
                                    >
                                      <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                  </Stack>
                                ) : (
                                  <label
                                    htmlFor="aadharCard"
                                  >
                                    <input
                                      type="file"
                                      accept=".png, .jpg, .jpeg , application/pdf"
                                      name="aadharCard.file"
                                      id="aadharCard"
                                      onChange={(event) => handleFileUpload(event, 'Aadhar Card')}
                                      style={{ display: 'none' }}
                                      disabled={!formik.values.aadharCard?.docNumber
                                        || !!formik.errors.aadharCard?.docNumber}
                                    />
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      component="span"
                                      style={{ width: 180 }}
                                      disabled={!formik.values.aadharCard?.docNumber
                                        || !!formik.errors.aadharCard?.docNumber}
                                    >
                                      Select
                                    </Button>
                                  </label>
                                )}
                              </>

                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                          >
                            {
                          aadharDoc?.docNumber ? (
                            <IconButton
                              title="Delete Optional"
                              color="primary"
                              onClick={
                          () => handleDeleteItem(aadharDoc?._id, 'Aadhar Card')
                        }
                              type="button"
                              style={{ padding: 0 }}
                            >
                              <TrashIcon />
                            </IconButton>
                          ) : (
                            <Button
                              color="primary"
                              variant="contained"
                              component="span"
                              onClick={() => addDocuments(formik.values.aadharCard)}
                              style={{ width: 180 }}
                              disabled={!formik.values.aadharCard.docNumber
                                || !formik.values.aadharCard.docFile
                                || !!formik.errors.aadharCard?.docNumber}
                            >
                              Upload
                            </Button>
                          )
                          }
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            {formik.values.panCard?.docType}
                            <Typography
                              display="contents"
                              color="red"
                            >
                              &nbsp;*
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {panCardDoc?.docNumber ? (
                              panCardDoc?.docNumber
                            ) : (
                              <InputField
                                error={Boolean(formik.touched.panCard?.docNumber
                                && formik.errors.panCard?.docNumber)}
                                fullWidth
                                required
                                helperText={formik.touched.panCard?.docNumber
                                && formik.errors.panCard?.docNumber}
                                name="panCard.docNumber"
                                onBlur={formik.handleBlur}
                                onChange={(event) => {
                                  if (event.target.value !== ' ') {
                                    formik.setFieldValue('panCard.docNumber', event.target.value);
                                  }
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {panCardDoc?.docNumber ? (
                              <FileName>
                                {panCardDoc?.file?.name}
                              </FileName>
                            ) : (
                              <>
                                {(formik.values.panCard?.file !== null) ? (
                                  <Stack
                                    direction="row"
                                  >
                                    <FileName>
                                      {formik.values.panCard?.file?.name}
                                    </FileName>
                                    <IconButton
                                      aria-label="delete"
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteSelectedFile('PAN Card')}
                                    >
                                      <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                  </Stack>
                                ) : (
                                  <label
                                    htmlFor="panCard"
                                  >
                                    <input
                                      type="file"
                                      accept=".png, .jpg, .jpeg , application/pdf"
                                      name="panCard.file"
                                      id="panCard"
                                      onChange={(event) => handleFileUpload(event, 'PAN Card')}
                                      style={{ display: 'none' }}
                                      disabled={!formik.values.panCard?.docNumber
                                      || !!formik.errors.panCard?.docNumber}
                                    />
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      component="span"
                                      style={{ width: 180 }}
                                      disabled={!formik.values.panCard?.docNumber
                                        || !!formik.errors.panCard?.docNumber}
                                    >
                                      Select
                                    </Button>
                                  </label>
                                )}
                              </>

                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                          >
                            {
                          panCardDoc?.docNumber ? (
                            <IconButton
                              title="Delete Optional"
                              color="primary"
                              onClick={
                            () => handleDeleteItem(panCardDoc?._id, 'PAN Card')
                          }
                              type="button"
                              style={{ padding: 0 }}
                            >
                              <TrashIcon />
                            </IconButton>
                          ) : (
                            <Button
                              color="primary"
                              variant="contained"
                              component="span"
                              onClick={() => addDocuments(formik.values.panCard)}
                              style={{ width: 180 }}
                              disabled={!formik.values.panCard.docNumber
                                || !formik.values.panCard.docFile
                                || !!formik.errors.panCard?.docNumber}
                            >
                              Upload
                            </Button>
                          )
                          }
                          </TableCell>
                        </TableRow>
                        { scope !== 'Technician' && (
                        <TableRow>
                          <TableCell>
                            {formik.values.gst.docType}
                            <Typography
                              display="contents"
                              color="red"
                            >
                              &nbsp;*
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {gstDoc?.docNumber ? (
                              gstDoc?.docNumber
                            ) : (
                              <InputField
                                error={Boolean(formik.touched.gst?.docNumber
                                && formik.errors.gst?.docNumber)}
                                fullWidth
                                helperText={formik.touched.gst?.docNumber
                                && formik.errors.gst?.docNumber}
                                name="gst.docNumber"
                                onBlur={formik.handleBlur}
                                onChange={(event) => {
                                  if (event.target.value !== ' ') {
                                    formik.setFieldValue('gst.docNumber', event.target.value);
                                  }
                                }}
                                value={formik.values.gst?.docNumber}
                              />

                            )}
                          </TableCell>
                          <TableCell>
                            {gstDoc?.docNumber ? (
                              <FileName>
                                {gstDoc?.file?.name}
                              </FileName>
                            ) : (
                              <>
                                {(formik.values.gst?.file !== null) ? (
                                  <Stack
                                    direction="row"
                                  >
                                    <FileName>
                                      {formik.values.gst?.file?.name}
                                    </FileName>
                                    <IconButton
                                      aria-label="delete"
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteSelectedFile('GST')}
                                    >
                                      <DeleteIcon fontSize="inherit" />
                                    </IconButton>
                                  </Stack>
                                ) : (
                                  <label
                                    htmlFor="gst"
                                  >
                                    <input
                                      type="file"
                                      accept=".png, .jpg, .jpeg , application/pdf"
                                      name="gst.file"
                                      id="gst"
                                      onChange={(event) => handleFileUpload(event, 'GST')}
                                      style={{ display: 'none' }}
                                      disabled={!formik.values.gst?.docNumber
                                        || !!formik.errors.gst?.docNumber}
                                    />
                                    <Button
                                      color="primary"
                                      variant="contained"
                                      component="span"
                                      style={{ width: 180 }}
                                      disabled={!formik.values.gst?.docNumber
                                        || !!formik.errors.gst?.docNumber}
                                    >
                                      Select
                                    </Button>
                                  </label>
                                )}
                              </>

                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                          >
                            {
                          gstDoc?.docNumber ? (
                            <IconButton
                              title="Delete Optional"
                              color="primary"
                              onClick={
                              () => handleDeleteItem(gstDoc?._id, 'GST')
                            }
                              type="button"
                              style={{ padding: 0 }}
                            >
                              <TrashIcon />
                            </IconButton>
                          ) : (
                            <Button
                              color="primary"
                              variant="contained"
                              component="span"
                              onClick={() => addDocuments(formik.values.gst)}
                              style={{ width: 180 }}
                              disabled={!formik.values.gst.docNumber
                              || !formik.values.gst.docFile
                              || !!formik.errors.gst?.docNumber}
                            >
                              Upload
                            </Button>
                          )
                          }
                          </TableCell>
                        </TableRow>

                        )}
                        { optionalDocs.length > 0 && (
                          optionalDocs.map((doc) => (
                            <TableRow>
                              <TableCell>
                                {doc.docType}
                              </TableCell>
                              <TableCell>
                                {doc.docNumber}
                              </TableCell>
                              <TableCell>
                                {doc.file?.name}
                              </TableCell>
                              <TableCell
                                align="center"
                              >
                                <IconButton
                                  title="Delete Optional"
                                  color="primary"
                                  onClick={
                                      () => handleDeleteItem(doc?._id, 'Optional')
                                    }
                                  type="button"
                                  style={{ padding: 0 }}
                                >
                                  <TrashIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>
              <Table
                size="small"
              >
                <TableBody>
                  {isOptionalClicked && (
                    <TableRow
                      sx={{
                        borderTop: '1px',
                        borderTopColor: 'lightgrey',
                        borderTopStyle: 'solid'
                      }}
                      hover
                    >
                      <TableCell
                        width="25%"
                      >
                        <InputField
                          error={Boolean(formik.touched.optional?.docType
                              && formik.errors.optional?.docType)}
                          fullWidth
                          helperText={formik.touched.optional?.docType
                              && formik.errors.optional?.docType}
                          placeholder="Document Type"
                          name="optional.docType"
                          onBlur={formik.handleBlur}
                          onChange={(event) => {
                            if (event.target.value !== ' ') {
                              formik.setFieldValue('optional.docType', event.target.value);
                            }
                          }}
                          value={formik.values.optional?.docType}
                        />
                      </TableCell>
                      <TableCell
                        width="25%"
                      >
                        <InputField
                          error={Boolean(formik.touched.optional?.docNumber
                              && formik.errors.optional?.docNumber)}
                          fullWidth
                          helperText={formik.touched.optional?.docNumber
                              && formik.errors.optional?.docNumber}
                          placeholder="Document Number"
                          name="optional.docNumber"
                          onBlur={formik.handleBlur}
                          onChange={(event) => {
                            if (event.target.value !== ' ') {
                              formik.setFieldValue('optional.docNumber', event.target.value);
                            }
                          }}
                          value={formik.values.optional?.docNumber}
                        />
                      </TableCell>
                      <TableCell
                        width="25%"
                      >
                        {(formik.values.optional?.file !== null) ? (
                          <Stack
                            direction="row"
                          >
                            <FileName>
                              {formik.values.optional?.file?.name}
                            </FileName>
                            <IconButton
                              aria-label="delete"
                              size="small"
                              color="error"
                              onClick={() => handleDeleteSelectedFile('')}
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <label
                            htmlFor="optional"
                          >
                            <input
                              type="file"
                              accept=".png, .jpg, .jpeg , application/pdf"
                              name="optional.file"
                              id="optional"
                              onChange={(event) => handleFileUpload(event, '')}
                              style={{ display: 'none' }}
                              disabled={!formik.values.optional?.docNumber
                                  || !formik.values.optional?.docType
                                  || !!formik.errors.optional?.docNumber
                                  || !!formik.errors.optional?.docType}
                            />
                            <Button
                              color="primary"
                              variant="contained"
                              fullWidth
                              component="span"
                              disabled={!formik.values.optional?.docNumber
                                  || !formik.values.optional?.docType
                                  || !!formik.errors.optional?.docNumber
                                  || !!formik.errors.optional?.docType}
                            >
                              Select
                            </Button>
                          </label>
                        )}
                      </TableCell>
                      <TableCell
                        width="25%"
                        align="center"
                      >
                        <Button
                          color="primary"
                          variant="contained"
                          component="span"
                          onClick={() => addDocuments(formik.values.optional)}
                          disabled={!formik.values.optional?.docNumber
                              || !formik.values.optional?.docType
                              || !!formik.errors.optional?.docNumber
                              || !!formik.errors.optional?.docType
                              || !formik.values.optional?.docFile}
                        >
                          Upload
                        </Button>
                        <IconButton
                          onClick={() => {
                            clearOptionalField();
                          }}
                          aria-label="close"
                        >
                          <CloseIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        sx={{
          alignItems: 'right'
        }}
      >
        <Button
          color="primary"
          onClick={(e) => {
            closeDialog(e, '');
          }}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
      <ConfirmationDialog
        message="Are you sure you want to delete this Document? This can't be undone."
        onCancel={() => {
          handleCloseDeleteDialog();
          setServiceAreaToBeDeleted({ id: '', type: '' });
        }}
        onConfirm={
            () => handleDocumentDelete(serviceAreaToBeDeleted.id, serviceAreaToBeDeleted.type)
          }
        open={deleteDialogOpen}
        title="Are You Sure?"
        variant="error"
      />
    </Dialog>
  );
};

AddKycDocuments.defaultProps = {
  open: false,
};

AddKycDocuments.propTypes = {
  // @ts-ignore
  open: PropTypes.bool,
  onClose: PropTypes.func,
  scope: PropTypes.string,
  documentDetails: PropTypes.array,
  addDocument: PropTypes.func,
  deleteDocument: PropTypes.func
};
